/**
 * Per-scope chunk contracts (TASK-710).
 *
 * Every chat scope must declare what it does with EVERY chunk type in the
 * streaming contract: handled by a scope handler, deliberately ignored, or one
 * of the hook's core types. chunkContract.test.ts asserts full coverage per
 * scope — an undeclared chunk type is a test failure, not a silent drop.
 *
 * Scopes pass their declaration straight into useAgenticChat (`ignored`), so
 * the declaration and the runtime behavior cannot drift apart.
 */

import type { StreamingChunkType } from './chat.types';

/** Handled inside useAgenticChat itself; scopes never declare these. */
export const CORE_CHUNK_TYPES: readonly StreamingChunkType[] = [
  'token',
  'done',
  'error',
  // Default canvasStore sink (OpenDocument → pending-open slot); a scope MAY
  // override it with its own `widget` handler.
  'widget',
] as const;

export interface ScopeChunkContract {
  handled: StreamingChunkType[];
  ignored: StreamingChunkType[];
}

export const SCOPE_CHUNK_CONTRACTS: Record<string, ScopeChunkContract> = {
  // The global dock's default scope (TASK-713): deep chat with artifacts,
  // app-control actions, sources, tool progress, hard-gate. Domain side-channel
  // chunks belong to scopes that migrate in 715/716/717.
  generic: {
    handled: [
      // TASK-790: `widget` is a CORE type, but the dock OVERRIDES it —
      // ArtifactCard creations stay inline in the conversation (TASK-768)
      // and everything else falls through to the canvas sink. Declared here
      // for the same reason `dashboard` declares it: this file exists so the
      // declaration and the runtime behaviour cannot drift apart.
      'widget',
      'artifact',
      'action',
      'source',
      'status',
      'tool_start',
      'tool_end',
      'hard_gate_confirm',
      // TASK-869: `propose_action` is granted on every surface that can claim a task,
      // and this chunk is its only visible product — ignoring it dropped the Apply card.
      'action_proposal',
    ],
    ignored: [
      'extraction_proposal',
      'proposal_action',
      'brief_update',
      'document_edit',
      'document_op',
      'spreadsheet_edit',
      'presentation_edit',
    ],
  },
  dashboard: {
    handled: [
      // widget is overridden: dock buffers specs and commits them with the
      // assistant bubble on done (preserves the TASK-651/707 refreshed-note).
      'widget',
      'status',
      'tool_start',
      'tool_end',
      'hard_gate_confirm',
      // TASK-715: with a meeting focus popup open, the dashboard dock scope-
      // switches to it and routes these into the meeting-scope store.
      'brief_update',
      'proposal_action',
      'action_proposal',
    ],
    ignored: [
      'artifact',
      'action',
      'source',
      'extraction_proposal',
      'document_edit',
      'document_op',
      'spreadsheet_edit',
      'presentation_edit',
    ],
  },
  // TASK-716: KYC workspace on the global dock (route shell /clients/:id/kyc-workspace).
  kyc: {
    handled: [
      // TASK-790: `widget` is a CORE type, but the dock OVERRIDES it —
      // ArtifactCard creations stay inline in the conversation (TASK-768)
      // and everything else falls through to the canvas sink. Declared here
      // for the same reason `dashboard` declares it: this file exists so the
      // declaration and the runtime behaviour cannot drift apart.
      'widget',
      'artifact',
      'action',
      'source',
      'status',
      'tool_start',
      'tool_end',
      'hard_gate_confirm',
      'extraction_proposal',
      // TASK-869: `propose_action` is granted on every surface that can claim a task,
      // and this chunk is its only visible product — ignoring it dropped the Apply card.
      'action_proposal',
    ],
    ignored: [
      'brief_update',
      'proposal_action',
      'document_edit',
      'document_op',
      'spreadsheet_edit',
      'presentation_edit',
    ],
  },
  // TASK-715: meeting prep on the global dock (route shell /meeting/:id/prepare).
  meeting: {
    handled: [
      // TASK-790: `widget` is a CORE type, but the dock OVERRIDES it —
      // ArtifactCard creations stay inline in the conversation (TASK-768)
      // and everything else falls through to the canvas sink. Declared here
      // for the same reason `dashboard` declares it: this file exists so the
      // declaration and the runtime behaviour cannot drift apart.
      'widget',
      'artifact',
      'action',
      'source',
      'status',
      'tool_start',
      'tool_end',
      'hard_gate_confirm',
      'brief_update',
      'proposal_action',
      'action_proposal',
    ],
    ignored: [
      'extraction_proposal',
      'document_edit',
      'document_op',
      'spreadsheet_edit',
      'presentation_edit',
    ],
  },
  workbench: {
    handled: [
      'document_edit',
      'document_op',
      'spreadsheet_edit',
      'presentation_edit',
      'hard_gate_confirm',
      // TASK-869: `propose_action` is granted on every surface that can claim a task,
      // and this chunk is its only visible product — ignoring it dropped the Apply card.
      'action_proposal',
    ],
    ignored: [
      'artifact',
      'action',
      'source',
      'status',
      'tool_start',
      'tool_end',
      'extraction_proposal',
      'proposal_action',
      'brief_update',
    ],
  },
};
